// Formatação da caixa de entrada.
//
// Tudo aqui é função pura, sem React e sem Supabase: é o que dá para testar com
// `node --test` (ver __tests__/formato.test.ts) e o que mais quebra em produção
// — telefone de tamanho inesperado, mensagem sem corpo, data de outro fuso.

import type { ChannelType, Direcao, MensagemTipo } from './tipos';

/**
 * Os canais como o ATENDENTE os enxerga, para o filtro da lista.
 *
 * O WhatsApp oficial e o não-oficial são o mesmo canal para quem atende — o
 * cliente não sabe (nem precisa saber) por qual dos dois escreveu. A diferença
 * técnica entre eles mora nas capabilities, não aqui.
 */
export const GRUPOS_DE_CANAL: { chave: string; rotulo: string; tipos: ChannelType[] }[] = [
  { chave: 'whatsapp', rotulo: 'WhatsApp', tipos: ['whatsapp_evolution', 'whatsapp_cloud'] },
  { chave: 'email', rotulo: 'E-mail', tipos: ['email'] },
  { chave: 'instagram', rotulo: 'Instagram', tipos: ['instagram'] },
];

/** Nomes dos meses em pt-BR, sem depender do locale do navegador. */
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const DIAS_SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/**
 * +5564992595552 → (64) 99259-5552.
 *
 * Número de fora do Brasil, ou curto demais para ter DDD, sai como veio: é
 * melhor mostrar o número cru do que inventar um formato errado.
 */
export function formatarTelefone(e164: string | null | undefined): string {
  if (!e164) return '';
  const so = e164.replace(/\D/g, '');
  if (!so.startsWith('55') || so.length < 12 || so.length > 13) return e164;
  const ddd = so.slice(2, 4);
  const numero = so.slice(4);
  if (numero.length === 9) return `(${ddd}) ${numero.slice(0, 5)}-${numero.slice(5)}`;
  if (numero.length === 8) return `(${ddd}) ${numero.slice(0, 4)}-${numero.slice(4)}`;
  return e164;
}

/** O que aparece como título da conversa. Contato sem nome cai no telefone. */
export function nomeExibicao(nome: string | null | undefined, telefone: string | null | undefined): string {
  const limpo = (nome ?? '').trim();
  if (limpo) return limpo;
  const tel = formatarTelefone(telefone);
  return tel || 'Contato sem identificação';
}

/** Iniciais para o avatar. Duas letras no máximo. */
export function iniciais(nome: string | null | undefined, telefone: string | null | undefined): string {
  const limpo = (nome ?? '').trim();
  if (!limpo) {
    const so = (telefone ?? '').replace(/\D/g, '');
    return so ? so.slice(-2) : '?';
  }
  const partes = limpo.split(/\s+/).filter(Boolean);
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

const ROTULO_TIPO: Partial<Record<MensagemTipo, string>> = {
  image: '📷 Foto',
  audio: '🎤 Áudio',
  video: '🎬 Vídeo',
  document: '📄 Documento',
  sticker: '💬 Figurinha',
  location: '📍 Localização',
};

/**
 * A linha de prévia na lista de conversas.
 *
 * Mensagem de mídia costuma vir com body null — sem este mapa a lista mostraria
 * uma linha em branco e o operador acharia que a conversa está vazia.
 */
export function previaMensagem(
  tipo: MensagemTipo | null | undefined,
  corpo: string | null | undefined,
  direcao?: Direcao | null,
): string {
  // Sem tipo NENHUM a conversa nunca recebeu mensagem — é diferente de uma
  // mensagem que existe e veio sem corpo (mídia).
  if (!tipo) return 'Sem mensagens';
  const texto = (corpo ?? '').replace(/\s+/g, ' ').trim();
  const base = texto || ROTULO_TIPO[tipo] || 'Mensagem';
  return direcao === 'outbound' ? `Você: ${base}` : base;
}

/** 14:32 */
export function horaCurta(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Diferença em dias de calendário — não em horas: 23h59 pode ser "ontem". */
function diasDeCalendario(alvo: Date, agora: Date): number {
  const a = new Date(alvo.getFullYear(), alvo.getMonth(), alvo.getDate());
  const b = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

/** Carimbo da lista: hoje vira hora, ontem vira "Ontem", a semana vira o dia. */
export function carimboLista(iso: string | null | undefined, agora: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dias = diasDeCalendario(d, agora);
  if (dias <= 0) return horaCurta(iso);
  if (dias === 1) return 'Ontem';
  if (dias < 7) return DIAS_SEMANA[d.getDay()].slice(0, 3);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

/** Separador entre os dias, dentro da thread. */
export function separadorDeDia(iso: string, agora: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dias = diasDeCalendario(d, agora);
  if (dias <= 0) return 'Hoje';
  if (dias === 1) return 'Ontem';
  const base = `${d.getDate()} de ${MESES[d.getMonth()]}`;
  return d.getFullYear() === agora.getFullYear() ? base : `${base} de ${d.getFullYear()}`;
}

/** Chave de agrupamento por dia local (não UTC: senão a virada sai errada). */
export function chaveDoDia(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'invalido';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 412839 → "403 KB" */
export function tamanhoLegivel(bytes: number | null | undefined): string {
  if (!bytes || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Há quanto tempo a conexão deu sinal de vida. O heartbeat do backend marca a
 * conexão como parada depois de 5 minutos — o banner precisa contar a mesma
 * história antes disso, senão o operador confia num canal que já caiu.
 */
export function desdeQuando(iso: string | null | undefined, agora: Date = new Date()): string {
  if (!iso) return 'nunca';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'nunca';
  const seg = Math.max(0, Math.round((agora.getTime() - d.getTime()) / 1000));
  if (seg < 60) return 'agora há pouco';
  if (seg < 3600) return `há ${Math.floor(seg / 60)} min`;
  if (seg < 86400) return `há ${Math.floor(seg / 3600)} h`;
  return `há ${Math.floor(seg / 86400)} d`;
}

/**
 * Prazo de SLA em linguagem de gente: "em 45 min", "atrasado há 2 h".
 *
 * O sinal importa mais que o número — quem olha precisa saber, num relance, se
 * ainda dá tempo. Por isso o texto vem junto com o `estourado`, e não uma data
 * seca que obriga a pessoa a fazer a conta de cabeça.
 */
export function prazoRelativo(
  iso: string | null | undefined,
  agora: Date = new Date(),
): { texto: string; estourado: boolean } {
  if (!iso) return { texto: 'sem prazo', estourado: false };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { texto: 'sem prazo', estourado: false };

  const diff = d.getTime() - agora.getTime();
  const estourado = diff < 0;
  const min = Math.floor(Math.abs(diff) / 60000);

  let quanto: string;
  if (min < 1) quanto = 'menos de 1 min';
  else if (min < 60) quanto = `${min} min`;
  else if (min < 60 * 24) quanto = `${Math.floor(min / 60)} h`;
  else quanto = `${Math.floor(min / (60 * 24))} d`;

  return { texto: estourado ? `atrasado há ${quanto}` : `em ${quanto}`, estourado };
}
